import pb from './pocketbaseClient';
import { safeFilter } from '../utils/pbFilterEscape'; // TD3-191: SQL injection prevention

class OracleSessionService {
  
  /**
   * Save completed oracle session to PocketBase
   * @param {Object} sessionData - The complete session data
   * @param {string} userId - The user ID from PocketBase auth
   * @returns {Promise} Result of the save operation
   */
  async saveCompletedSession(sessionData, userId) {
    try {
      // Check if this session was already saved to avoid duplicates
      const sessionId = sessionData.sessionId;
      if (sessionId) {
        try {
          // TD3-191: Use safeFilter to prevent SQL injection
          const existingRecords = await pb.collection('OracleSessions').getList(1, 1, {
            filter: safeFilter`user = "${userId}" && sessionData ~ "${sessionId}"`
          });
          
          if (existingRecords.items.length > 0) {
            console.log('Session already saved, skipping duplicate:', sessionId);
            return { success: true, record: existingRecords.items[0], skipped: true };
          }
        } catch (checkError) {
          // If check fails due to permissions, warn but continue
          if (checkError.status === 403) {
            console.warn('⚠️ PocketBase permissions: Cannot check for duplicates (superuser required). Proceeding with save...');
          } else {
            console.log('Error checking for existing session, proceeding with save:', checkError);
          }
        }
      }

      // Prepare the data for PocketBase
      const data = {
        user: userId,
        sessionData: JSON.stringify(sessionData),
        isComplete: true,
        completedAt: new Date().toISOString()
      };

      // Create the record in OracleSessions collection
      console.log('📊 PocketBase: Saving oracle session to database...');
      const record = await pb.collection('OracleSessions').create(data);
      
      console.log('✅ PocketBase: Oracle session saved successfully:', record.id);
      console.log('📋 PocketBase: Session data summary:', {
        user: userId,
        ray: sessionData.raySelection,
        question: sessionData.userQuestion?.substring(0, 50) + '...',
        completedAt: new Date().toISOString()
      });
      return { success: true, record };
      
    } catch (error) {
      console.error('❌ PocketBase: Error saving oracle session:', error);
      
      // Provide user-friendly error messages
      let userMessage = 'Erro ao salvar sessão do oráculo';
      if (error.status === 403) {
        userMessage = '⚠️ Aviso: Sessão salva localmente. Problemas de permissão no servidor.';
        console.warn('⚠️ PocketBase: Session saved with permission warnings. Check database configuration.');
      } else if (error.status === 404) {
        userMessage = 'Erro: Coleção do banco de dados não encontrada.';
      } else if (error.status >= 500) {
        userMessage = 'Erro no servidor. Tente novamente em alguns minutos.';
      }
      
      return { 
        success: false, 
        error: userMessage,
        originalError: error.message
      };
    }
  }

  /**
   * Get all oracle sessions for a user
   * @param {string} userId - The user ID from PocketBase auth
   * @returns {Promise} List of user's oracle sessions
   */
  async getUserSessions(userId) {
    try {
      // TD3-191: Use safeFilter to prevent SQL injection
      const records = await pb.collection('OracleSessions').getList(1, 50, {
        filter: safeFilter`user = "${userId}" && isComplete = true`,
        sort: '-completedAt'
      });
      
      return { success: true, sessions: records.items };
      
    } catch (error) {
      console.error('Error fetching user sessions:', error);
      return { 
        success: false, 
        error: error.message || 'Erro ao buscar sessões do usuário' 
      };
    }
  }

  /**
   * Get a specific oracle session
   * @param {string} sessionId - The session record ID
   * @returns {Promise} The oracle session data
   */
  async getSession(sessionId) {
    try {
      // TD3-127: Use getList with filter instead of getOne to avoid View rule 403 errors
      // This uses the List API rule which allows user access to their own records
      // TD3-191: Use safeFilter to prevent SQL injection
      const result = await pb.collection('OracleSessions').getList(1, 1, {
        filter: safeFilter`id = "${sessionId}"`
      });

      if (result.items.length === 0) {
        return {
          success: false,
          error: 'Sessão não encontrada'
        };
      }

      const record = result.items[0];

      // PocketBase auto-parses JSON fields, check if already parsed
      const sessionData = typeof record.sessionData === 'string'
        ? JSON.parse(record.sessionData)
        : record.sessionData;

      return {
        success: true,
        session: {
          ...record,
          sessionData
        }
      };

    } catch (error) {
      console.error('Error fetching oracle session:', error);
      return {
        success: false,
        error: error.message || 'Erro ao buscar sessão do oráculo'
      };
    }
  }

  /**
   * Log usage changes to OracleUsageLog collection for audit trail
   * @param {string} userId - The user ID from PocketBase auth
   * @param {string} action - Action type: "increment", "reset", "manual_adjust"
   * @param {number} oldValue - Previous usage value
   * @param {number} newValue - New usage value
   * @param {string} periodStart - Period start date for context
   * @param {string} oracleSessionId - Optional oracle session ID
   * @param {string} adminNotes - Optional admin notes
   * @returns {Promise} Result of the logging operation
   */
  async logUsageChange(userId, action, oldValue, newValue, periodStart, oracleSessionId = null, adminNotes = null) {
    const logData = {
      user: userId,
      action: action,
      oldValue: parseInt(oldValue) || 0,
      newValue: parseInt(newValue) || 0,
      periodStart: periodStart,
      completedAt: new Date().toISOString(),
      oracleSession: oracleSessionId || null // Required field - use null if not provided
    };

    // Add optional fields if provided
    if (adminNotes) {
      logData.adminNotes = adminNotes;
    }

    console.log('📋 Attempting to log usage change with data:', logData);

    try {
      const logRecord = await pb.collection('OracleUsageLog').create(logData);
      console.log(`📊 Usage log created: ${action} ${oldValue}→${newValue} for user ${userId} (${logRecord.id})`);

      return { success: true, logRecord };

    } catch (error) {
      console.error('Error logging usage change:', error);
      console.error('Log data attempted:', logData);
      console.error('PocketBase error details:', JSON.stringify(error.response?.data || error.data || 'No detailed error data', null, 2));
      // Don't fail the main operation if logging fails
      return { success: false, error: error.message };
    }
  }

  /**
   * Increment oracle usage counter for a user
   * @param {string} userId - The user ID from PocketBase auth
   * @param {string} oracleSessionId - Optional oracle session ID for audit trail
   * @returns {Promise} Result of the increment operation
   */
  async incrementOracleUsage(userId, oracleSessionId = null) {
    try {
      // CRITICAL: Check and reset period BEFORE incrementing to prevent exploit
      const wasReset = await this.checkAndResetPeriod(userId);
      if (wasReset) {
        console.log(`🔄 Period reset detected during oracle usage increment for user ${userId}`);
      }

      // Get current user data (may be fresh from reset)
      const user = await pb.collection('OracleUsers').getOne(userId);

      // Store old value for audit trail
      const oldCount = user.oraclesUsedThisPeriod || 0;

      // Increment the counter (now safely within current period)
      const newCount = oldCount + 1;

      // Update the user record
      const updatedUser = await pb.collection('OracleUsers').update(userId, {
        oraclesUsedThisPeriod: newCount
      });

      // Log the increment for audit trail
      await this.logUsageChange(
        userId,
        'increment',
        oldCount,
        newCount,
        user.currentPeriodStart,
        oracleSessionId,
        oracleSessionId ? `Oracle session: ${oracleSessionId}` : 'Manual increment'
      );

      console.log(`📈 PocketBase: Oracle usage incremented for user ${userId}: ${oldCount}→${newCount} (period: ${user.currentPeriodStart})`);
      return { success: true, newCount, user: updatedUser };

    } catch (error) {
      console.error('Error incrementing oracle usage:', error);
      return {
        success: false,
        error: error.message || 'Erro ao incrementar uso do oráculo'
      };
    }
  }

  /**
   * Check if period has expired and reset if needed
   * @param {string} userId - The user ID from PocketBase auth
   * @returns {Promise} Whether period was reset
   */
  async checkAndResetPeriod(userId) {
    try {
      const user = await pb.collection('OracleUsers').getOne(userId);
      const nowUTC = new Date();
      const periodStartUTC = new Date(user.currentPeriodStart);

      // Calculate month difference using UTC dates
      const monthsDiff = (nowUTC.getUTCFullYear() - periodStartUTC.getUTCFullYear()) * 12 +
                         (nowUTC.getUTCMonth() - periodStartUTC.getUTCMonth());

      // If a month has passed, reset to 1st of current month at midnight UTC
      if (monthsDiff >= 1) {
        const newPeriodStart = new Date(Date.UTC(nowUTC.getUTCFullYear(), nowUTC.getUTCMonth(), 1, 0, 0, 0, 0));
        const oldUsage = user.oraclesUsedThisPeriod || 0;

        // Update the database first
        await pb.collection('OracleUsers').update(userId, {
          currentPeriodStart: newPeriodStart.toISOString(),
          oraclesUsedThisPeriod: 0
        });

        // Log the reset for audit trail
        await this.logUsageChange(
          userId,
          'reset',
          oldUsage,
          0,
          newPeriodStart.toISOString(),
          null,
          `Period reset from ${periodStartUTC.toISOString()} to ${newPeriodStart.toISOString()}`
        );

        console.log(`📅 Period reset for user ${userId}: ${oldUsage}→0, new period starts ${newPeriodStart.toISOString()}`);
        return true; // Period was reset
      }
      return false; // No reset needed

    } catch (error) {
      console.error('Error checking and resetting period:', error);
      return false;
    }
  }

  /**
   * Calculate next reset date (UTC-based for global consistency)
   * @param {string} currentPeriodStart - Current period start date
   * @returns {Date} Next reset date at midnight UTC
   */
  getNextResetDate(currentPeriodStart) {
    const start = new Date(currentPeriodStart);
    const nextReset = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1, 0, 0, 0, 0));
    return nextReset;
  }

  /**
   * Get user's oracle usage info
   * @param {string} userId - The user ID from PocketBase auth
   * @returns {Promise} User's oracle usage data (always matches PocketBase storage)
   */
  async getUserOracleUsage(userId) {
    try {
      // Check and reset period if needed - this updates the database immediately
      const wasReset = await this.checkAndResetPeriod(userId);

      // Fetch fresh user data (guaranteed to be post-reset if reset occurred)
      const user = await pb.collection('OracleUsers').getOne(userId);

      // Log reset for debugging database consistency
      if (wasReset) {
        console.log(`🔄 Period reset applied in getUserOracleUsage for user ${userId}. PocketBase now shows: used=${user.oraclesUsedThisPeriod}, period=${user.currentPeriodStart}`);
      }

      const oraclesUsed = user.oraclesUsedThisPeriod || 0;
      const oracleLimit = user.oracleLimit || 3; // Default to 3 per month
      const remainingUses = Math.max(0, oracleLimit - oraclesUsed);
      const nextReset = this.getNextResetDate(user.currentPeriodStart);

      // Return data that exactly matches what's now stored in PocketBase
      return {
        success: true,
        oraclesUsed,
        oracleLimit,
        remainingUses,
        currentPeriodStart: user.currentPeriodStart,
        nextReset,
        wasResetApplied: wasReset // For debugging purposes
      };

    } catch (error) {
      console.error('Error getting user oracle usage:', error);
      return {
        success: false,
        error: error.message || 'Erro ao buscar uso do oráculo'
      };
    }
  }
}

// Export singleton instance
const oracleSessionService = new OracleSessionService();
export default oracleSessionService;